import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import LeadsContext from '../../providers/Leads';
import LeadMobileCard from './components/LeadMobileCard';
import ResolvePagination from 'components/ResolvePagination';
import { SortingButton } from 'router/subrouters/DataRoom/components/SortingButton';
import { useTranslation } from 'react-i18next';
import useQueryParams from 'hooks/useQueryParams';
import { SORTING_VALUES } from '../LeadsTableContainer/data';
import ShowInactiveLeads from '../ShowInactiveLeads';
import { Lead } from 'models/Lead';
import {
  INACTIVE_PAGE,
  ONGOING_PAGE,
} from '../../providers/Leads/Leads.provider';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import SettingsIcon from 'icons/Settings.icon';
import {
  FILE_ACCESS_SETTINGS_BUTTON_ID,
  FIRST_RELEASE_DISCOVERING_FEATURE,
} from 'data/discoveringFeature';
import { hasNotSeenDiscoveryFeature } from 'utils';

import './LeadsMobileList.styles.scss';

type LeadsMobileListProps = {
  className?: string;
};

const LeadsMobileList: React.FC<LeadsMobileListProps> = (props) => {
  const { className } = props;

  const {
    totalOngoingPages,
    totalWaitlistedPages,
    onGoingTableLeads,
    waitlistedTableLeads,
    showInactiveLeads,
    numberOfWaitlistedGridLeads,
    showFileAccessSettings,
    openReorderFilesDrawer,
    updateLead,
    openLeadDrawer,
  } = useContext(LeadsContext);

  const {
    currentUser: { seenFileReorderFeature },
  } = useContext(CurrentUserContext);

  const classes = classNames('leads-mobile-list', className);

  const { t } = useTranslation();

  const {
    params: { sort },
    setQueryParam,
  } = useQueryParams();

  const updateSort = useCallback(
    (sort) => setQueryParam('sort', sort),
    [setQueryParam],
  );

  const renderLeads = useCallback(
    (leads: Lead[]) =>
      leads.map((lead) => (
        <LeadMobileCard
          key={lead?._id}
          lead={lead}
          onClick={() => openLeadDrawer(lead)}
          updateLead={updateLead}
        />
      )),
    [openLeadDrawer, updateLead],
  );

  return (
    <div className={classes}>
      <div className="leads-mobile-list__sort">
        <SortingButton
          content={t('aToZ')}
          sortingValue={SORTING_VALUES.name}
          sort={sort}
          updateSort={updateSort}
        />

        <SortingButton
          content={t('lastSeen')}
          sortingValue={SORTING_VALUES.lastSeen}
          sort={sort}
          updateSort={updateSort}
        />

        {showFileAccessSettings && (
          <Button
            variant="icon"
            id={FILE_ACCESS_SETTINGS_BUTTON_ID}
            icon={SettingsIcon}
            className={classNames('leads-mobile-list__file-access-settings', {
              'new-feature': hasNotSeenDiscoveryFeature(
                seenFileReorderFeature,
                FIRST_RELEASE_DISCOVERING_FEATURE,
              ),
            })}
            onClick={openReorderFilesDrawer}
          />
        )}
      </div>

      {renderLeads(onGoingTableLeads)}

      <ResolvePagination
        pageParam={ONGOING_PAGE}
        totalPages={totalOngoingPages}
      />

      <ShowInactiveLeads />

      {showInactiveLeads && !!numberOfWaitlistedGridLeads && (
        <>
          {renderLeads(waitlistedTableLeads)}

          <ResolvePagination
            pageParam={INACTIVE_PAGE}
            totalPages={totalWaitlistedPages}
          />
        </>
      )}
    </div>
  );
};

export default LeadsMobileList;
